import { BaseWebComponent } from "../base-components/base-web-component.js";
import { UploadClientComponent, UploadResult, UploadClientFileSelectedEvent, UploadClientCancelRequestedEvent, UploadClientUploadCompleteEvent, formatFileSize } from '@bonusmap/fileupload/client';
import type { ApiResponse, User, FileArchiveDefRow, FileArchiveDefRowNoId, FileArchiveDefRowInfo, RemoveFileResponseData } from "@bonusmap/api-types";
import { fileArchiveDefSchema, fileArchiveDefSchemaNoId, numericSchema } from "@bonusmap/api-types";
import { NunjucksEnvCallbacks, templateParse } from "../../logic/templateParse.js";
import { showToast, toastError, toastSuccess } from "../../toasts.js";
import { z } from "zod";
import { showConfirm } from "../../modals.js";
import { BonusFileEventMap } from "./bonus-file-events.js";
import { isNumber } from "@bonusmap/shared_ui/library";
import { createChoicesInstance } from "../../utils/createChoicesInstance.js";
const { Choices } = window;
/**
 * Stati del componente
 * inserting: quando si sta inserendo un nuovo file (fileId non settato)
 * inserting-loaded: quando si è in inserting e si è caricato il file 
 * editing: quando si sta modificando un file esistente (fileId settato)
 */
const bonusFileComponentStates = ['inserting', 'inserting-loaded', 'editing'] as const;
type BonusFileComponentState = 'inserting' | 'inserting-loaded' | 'editing';
/**
 * Risultato del caricamento del file
 */
// const numericSchema = z.union([z.number(), z.string().regex(/^\d+$/).transform(val => parseInt(val, 10))]);
const uploadResultSchema = z.object({
    id: numericSchema,
    filename: z.string(),
    finalPath: z.string(),
    finalType: z.string().optional(),
    finalSize: numericSchema
});
export class BonusFileComponent extends BaseWebComponent {
    // =============================
    // <bonus-file/>
    static webComponentName: string = "bonus-file";
    // =============================
    static observedAttributes = ['file-id'];
    // =============================
    protected _templateId: string = "bonus-file"; // Template ID for rendering
    protected get templateId(): string {
        if (!this.user || this.adminNotImpersonating) {
            return "bonus-file-no-user";
        }
        if (this.fileId) {
            return "bonus-file-view";
        }

        return this._templateId;
    }
    // =============================
    // Elements
    private tagSelectEl!: HTMLSelectElement;
    private choicesInstance!: InstanceType<Window['Choices']>; // Choice.js instance
    private fileInputElement!: UploadClientComponent | null;
    private idEl!: HTMLInputElement | null;
    private organizationIdEl!: HTMLInputElement | null;
    private filePathEl!: HTMLInputElement | null;
    private fileNameEl!: HTMLInputElement | null;
    private fileTypeEl!: HTMLInputElement | null;
    private fileSizeEl!: HTMLInputElement | null;
    private labelEl!: HTMLInputElement | null;
    private descriptionEl!: HTMLInputElement | null;
    private btnResetEl!: HTMLButtonElement | null;
    private btnSubmitEl!: HTMLButtonElement | null;
    public get fileId(): string | null {
        if (this.hasAttribute('file-id') && isNumber(this.getAttribute('file-id'))) {
            return this.getAttribute('file-id');
        }
        return null;
    }

    get readOnly(): boolean {
        return this.hasAttribute('readonly') || this.closest('[readonly]') !== null;
    }



    private _value: FileArchiveDefRow | null = null;
    public get value(): FileArchiveDefRow | null {
        return this._value;
    }
    public set value(value: FileArchiveDefRow | null) {
        const parsedValue = value ? this.parseFileArchiveDefRow(value) : null;
        this._value = parsedValue ? parsedValue : null;
        this.render();
    }

    private parseFileArchiveDefRow(data: any): FileArchiveDefRow | null {
        const result = fileArchiveDefSchema.safeParse(data);
        if (!result.success) {
            result.error.errors.forEach(err => {
                console.error(`Error at ${err.path.join('.')} : ${err.message}`);
            });
            console.error("Invalid FileArchiveDefRow data:", result.error.errors);
            return null;
        }
        return result.data;
    }

    private parseFileArchiveDefRowNoId(data: any): FileArchiveDefRowNoId | null {
        const result = fileArchiveDefSchemaNoId.safeParse(data);
        if (!result.success) {
            console.error("Invalid FileArchiveDefRowNoId data:", result.error.errors);
            return null;
        }
        return result.data;
    }
    constructor() {
        super();
        // impostazione iniziale stato componente
        if (this.fileId) {
            this.componentState = 'editing';
        }
        else {
            this.componentState = 'inserting';
        }
        this.initGlobalEvents();
    }
    private initGlobalEvents() {
        this.on("click", `button[role="delete"]`, async (e) => {
            e.preventDefault();
            if (this.readOnly) {
                return;
            }
            if (!this.fileId) {
                console.warn("No file ID to remove.");
                return;
            }
            showConfirm("Sei sicuro di voler rimuovere questo file?", {
                "title": "Conferma rimozione file"
            })
                .then((value) => {
                    if (value) {
                        const fileIdNum = parseInt(this.fileId ?? '0', 10);
                        if (!isNumber(fileIdNum)) {
                            console.error("Invalid file ID:", this.fileId);
                            return;
                        }
                        this.removeFile(fileIdNum); // procedi con la rimozione
                    }
                })
                .catch((err) => {
                    console.error("Error in confirmation dialog:", err);
                });

        });

    }
    private _componentState!: BonusFileComponentState;
    public get componentState(): BonusFileComponentState {
        return this._componentState;
    }
    private set componentState(value: BonusFileComponentState) {
        if (!bonusFileComponentStates.includes(value)) {
            console.warn(`Invalid component state: ${value}`);
            return;
        }
        // toglie tutte le classi di stato
        bonusFileComponentStates.forEach(state => {
            this.classList.remove(state);
        });
        // aggiunge la classe di stato corrente
        this.classList.add(value);
        this._componentState = value;
    }

    protected _nunjucksEnvCallbacks!: NunjucksEnvCallbacks;
    get nunjucksEnvCallbacks(): NunjucksEnvCallbacks {
        if (!this._nunjucksEnvCallbacks) {
            this._nunjucksEnvCallbacks = {
                filters: {},
                globals: {
                    fileActionUrl: (fileDef: { file_name?: string; file_type?: string; }) => {
                        // a seconda del tipo di file ritorna 'open' o 'download' open apre nel browser, download forza il download
                        const file_name = fileDef?.file_name ?? "";
                        const file_type = fileDef?.file_type ?? "";
                        if (file_type || file_name) {
                            const extension = file_name.split('.').pop()?.toLowerCase() ?? "";
                            try {
                                // Mimetype che si aprono direttamente nel browser
                                const browserSupportedTypes = [
                                    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
                                    'application/pdf'
                                ];

                                const browserSupportedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'pdf', 'txt', 'html'];

                                if (browserSupportedTypes.includes(file_type) || browserSupportedExtensions.includes(extension)) {
                                    return `open`;
                                }
                            } catch (error) {
                                console.error('Error determining file action:', error);
                            }
                        }
                        return `download`;
                    },
                    fileIconClass: (fileDef: { file_name: string; file_type: string; }) => {
                        let cls = "file";
                        const file_name = fileDef?.file_name ?? "";
                        const file_type = fileDef?.file_type ?? "";
                        const extension = file_name.split('.').pop()?.toLowerCase() ?? "";
                        if (file_type.startsWith('image/') || ['png', 'jpg', 'heic', 'bmp', 'webp', 'gif', 'jpeg'].includes(extension)) {
                            cls += " file-image";
                        }
                        else if (file_type === 'application/pdf' || extension === 'pdf') {
                            cls += " file-pdf";
                        }
                        else if (['doc', 'docx', 'odt', 'txt', 'rtf', 'pages', 'tex', 'wpd', 'wps'].includes(extension) || file_type === 'application/msword' || file_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || file_type === 'application/vnd.oasis.opendocument.text' || file_type === 'text/plain' || file_type === 'text/rtf') {
                            cls += " file-word";
                        }
                        else if (['xls', 'xlsx', 'csv', 'ods', 'tsv', 'xlsm', 'xlsb'].includes(extension) || file_type === 'application/vnd.ms-excel' || file_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file_type === 'text/csv' || file_type === 'application/vnd.oasis.opendocument.spreadsheet') {
                            cls += " file-excel";
                        }

                        else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension) || ['application/zip', 'application/x-rar-compressed', 'application/gzip'].includes(file_type)) {
                            cls += " file-zip";
                        }
                        return cls;
                    }
                }
            };
        }
        return this._nunjucksEnvCallbacks;
    }
    private async getFileInfo(fileId: number): Promise<FileArchiveDefRowInfo | null> {
        try {
            const response: ApiResponse<FileArchiveDefRowInfo> = await window.api.getFileInfo(fileId) as ApiResponse<FileArchiveDefRowInfo>;
            if (response.success && response.data) {
                return response.data as FileArchiveDefRowInfo;
            } else {
                return null;
            }
        } catch (error) {
            console.error("Network or unexpected error fetching file data:", error);
            return null;
        }
    }
    private async removeFile(fileId: number): Promise<RemoveFileResponseData | null> {
        try {
            const response: ApiResponse<RemoveFileResponseData> = await window.api.removeFile(fileId);
            if (response.success && response.data) {
                toastSuccess("File rimosso con successo.");
                this.remove();
                this.dispatch<"file-removed">("file-removed", { fileId: response.data.fileId });
                this.dispatch<"changed">("changed", {});
                return response.data;
            }
            else {
                toastError(`Errore rimozione file: ${response.message}`);
                return null;
            }
        } catch (error) {
            console.error("Network or unexpected error removing file:", error);
            toastError("Errore di rete o inaspettato durante la rimozione del file.");
            return null;
        }
    }
    /**
   * Overload per dispatch con tipizzazione personalizzata per EventMap
   */
    dispatch<K extends keyof BonusFileEventMap>(
        eventName: K,
        detail: BonusFileEventMap[K],
        bubbles?: boolean,
        cancelable?: boolean
    ): CustomEvent;

    dispatch<Eventname extends string = string>(
        eventName: Eventname,
        detail: any,
        bubbles = true,
        cancelable = true)
        : CustomEvent {
        return super.dispatch(eventName, detail, bubbles, cancelable);
    }

    protected async buildRenderingData(): Promise<Record<string, any>> {
        if (!this.user) {
            return {};
        }
        else if (this.adminNotImpersonating) {
            return {
                adminNotImpersonating: true
            };
        }
        const baseData = {
            organization_id: this.organizationId,
            idBase: this.createRandomId(),
            readonly: this.readOnly
        };
        if (this.fileId) {
            let fileData = {} as FileArchiveDefRowInfo | null;
            let file_size_formatted = "";
            if (isNumber(this.fileId)) {
                fileData = await this.getFileInfo(Number(this.fileId));
                if (fileData?.exists) {
                    const file_size = fileData?.file_size ?? 0;
                    file_size_formatted = formatFileSize(file_size);
                    this._value = fileData as FileArchiveDefRow;
                }
            }
            return {
                ...baseData,
                fileDef: fileData ? {
                    ...fileData,
                    file_size_formatted
                } : { ...baseData },
            }
        }
        return {
            ...baseData,
            fileDef: this.value
        }
    }

    private initializeChoices(): void {
        this.choicesInstance = createChoicesInstance(this.tagSelectEl, {
            addItemFilter: (value) => {
                return value.trim().length > 0;
            },
            removeItemButton: true,
            addItems: true,
            editItems: true,
            duplicateItemsAllowed: false,
            placeholder: true,
            placeholderValue: 'Scrivi per aggiungere tag...',
            addChoices: true,
            noChoicesText: 'Nessun tag disponibile'
        });
    }
    
    public async setBase64File(base64: string, filename: string, filetype?: string,submitInfo=true): Promise<boolean> {
        if (this.fileInputElement) {
            const success = await this.fileInputElement.setBase64File(base64, filename, filetype, true);
            if(success && submitInfo) {
                await this.submitHandler();
                return success;
            }
            return success;
        }
        return false;
    }

    private fileInputSetupListeners(): void {
        let labelLastSetFileInput = "";
        const setLabelIfEmpty = (file: File | string) => {
            const fileName = typeof file === 'string' ? file : file.name;
            if (this.labelEl && !this.labelEl.value) {
                this.labelEl.value = fileName;
                labelLastSetFileInput = fileName;
            }
        }

        this.fileInputElement?.addEventListener("file-selected", (event: UploadClientFileSelectedEvent) => {
            if (event.data) {
                setLabelIfEmpty(event.data);
            }
        });
        this.fileInputElement?.addEventListener("upload-cancelled", (e: Event) => {
            this.dispatch<"upload-cancelled">("upload-cancelled", {});
        });

        this.fileInputElement?.addEventListener("cancel-requested", (e: UploadClientCancelRequestedEvent) => {
            if (this.labelEl && this.labelEl.value === labelLastSetFileInput) {
                this.labelEl.value = "";
                labelLastSetFileInput = "";
            }
            this.dispatch<"cancel-requested">("cancel-requested", {});
        });

        this.fileInputElement?.addEventListener("upload-complete", (event: UploadClientUploadCompleteEvent) => {
            if (event.data) {
                //validate event.data with zod
                const parseResult = uploadResultSchema.safeParse(event.data);
                if (!parseResult.success) {
                    console.error("Invalid upload result data:", parseResult.error);
                    this.fileInputElement?.reset();
                    toastError("Errore nel risultato del caricamento del file.");
                    this.dispatch<"invalid-upload">("invalid-upload", { error: parseResult.error });
                    return;
                }
                else {
                    const filename = parseResult.data.filename;
                    const filepath = parseResult.data.finalPath;
                    const filesize = parseResult.data.finalSize;
                    const filetype = parseResult.data.finalType ?? "";
                    const fileid = parseResult.data.id;
                    // Aggiorna i campi nascosti con i dati del file caricato
                    setLabelIfEmpty(parseResult.data?.filename);
                    this.fileTypeEl!.value = filetype;
                    this.fileSizeEl!.value = filesize.toString();
                    this.filePathEl!.value = filepath;
                    this.fileNameEl!.value = filename;
                    this.idEl!.value = fileid.toString();
                    this.componentState = 'inserting-loaded';
                    this.dispatch<"file-uploaded">("file-uploaded", { filename, filepath, filesize, filetype, fileid });
                    toastSuccess("File caricato con successo.");
                }
            }
        })
    }
    submitHandler = async () => {
        const id = this.idEl?.value ? parseInt(this.idEl.value) : undefined;
        const organization_id = this.organizationIdEl?.value ? parseInt(this.organizationIdEl.value) : undefined;
        const file_path = this.filePathEl?.value ?? "";
        const file_name = this.fileNameEl?.value ?? "";
        const file_type = this.fileTypeEl?.value ? this.fileTypeEl.value : "";
        const file_size = this.fileSizeEl?.value ? parseInt(this.fileSizeEl.value) : 0;
        const label = this.labelEl?.value ?? "";
        const description = this.descriptionEl?.value ?? "";
        const tags = this.choicesInstance ? this.choicesInstance.getValue(true) as string[] : [];
        const fileData: FileArchiveDefRow = {
            id: (id as number),
            organization_id: (organization_id as number),
            file_path,
            file_name,
            file_type,
            file_size,
            label,
            description,
            tags
        };
        try {
            const apiResponse = await window.api.manager.fileArchive.update(fileData);
            if (apiResponse.success && apiResponse.data) {
                this.value = apiResponse.data as any; // Aggiorna il valore con i dati ritornati dall'API            
                const responseId = apiResponse.data?.id.toString() ?? "";
                this.setAttribute('file-id', responseId ? responseId : '');
                if (responseId == this.fileId) {
                    this.render(); // Rerender only if the fileId hasn't changed
                    //questo perchè se è cambiato il fileId, il render viene fatto automaticamente dalla onAttributeChange
                }
                this.dispatch<"changed">("changed", {});
                toastSuccess("File salvato con successo.");
            }
            else {
                toastError(apiResponse.message || "Errore durante il salvataggio del file.");
            }
        } catch (error) {
            toastError("Errore durante il salvataggio del file.");
        }

    };
    protected afterRender(): void {
        this.tagSelectEl = this.querySelector(`#${this.renderingData.idBase}-tags`)!;
        if (this.tagSelectEl) {
            this.initializeChoices();
        }
        this.fileInputElement = this.querySelector(UploadClientComponent.webComponentName);
        this.fileInputSetupListeners();
        this.organizationIdEl = this.querySelector(`[name="organization_id"]`);
        this.filePathEl = this.querySelector(`[name="file_path"]`);
        this.fileNameEl = this.querySelector(`[name="file_name"]`);
        this.fileTypeEl = this.querySelector(`[name="file_type"]`);
        this.fileSizeEl = this.querySelector(`[name="file_size"]`);
        this.labelEl = this.querySelector(`[name="label"]`);
        this.idEl = this.querySelector(`[name="id"]`);
        this.descriptionEl = this.querySelector(`[name="description"]`);
        this.btnResetEl = this.roleElementSelector("reset") as HTMLButtonElement;
        this.btnSubmitEl = this.roleElementSelector("submit") as HTMLButtonElement;
        this.btnSubmitEl?.addEventListener('click', this.submitHandler.bind(this));
    }

    protected async render(): Promise<void> {
        this.renderingData = await this.buildRenderingData();
        const html = templateParse(this.templateId, this.renderingData, this.nunjucksEnvCallbacks);
        if (html) {
            this.innerHTML = html;
            this.afterRender();
        } else {
            console.error(`Error rendering template '${this.templateId}' for ${this.tagName}`);
            this.onRenderError();
        }
    }
    connectedCallback(): void {
        // annulla il render spostato nell'attributo file-id
        if (!this.hasAttribute('file-id')) {
            // solo se non è presente l'attributo file-id
            super.connectedCallback();
        }
    }
    protected onAttributeChange(name: string, oldValue: string, newValue: string): void {
        if (name === 'file-id' && oldValue !== newValue) {
            this.render();

        }
    }

}